<!-- used for running / testing in isolation -->
<template>
  <SearchBar
    :type-of="typeOf"
    :separate-outlets="separateOutlets"
    :use-msp-locator-api="useMspLocatorApi"
    :area-rug-items="hasAreaRugItems"
    @zipcodeSubmitted="(zipCode)=>$emit('zipcodeSubmitted',zipCode)"
  />
  <div
    v-if="isLoading"
    class="text-center margin-bottom-xxs margin-top-xxs"
  >
    <div class="spinner-loader" />
  </div>
  <div v-if="btItems">
    <div class="margin-top-xs medium-margin-bottom-m small-margin-bottom-s">
      This item is on display at the following stores. Style and color may vary.*
    </div>
    <hr>
  </div>
  <div v-else-if="hasAreaRugItems">
    <div class="margin-top-xs medium-margin-bottom-m small-margin-bottom-s">
      Macy’s stores with rug departments within {{ resultsDisplayed }} miles
    </div>
    <hr>
  </div>
  <Stores
    v-if="!isLoading"
    :my-store-id="myStoreId"
    :bag-items="bagItems"
    :my-store-button="myStoreButton"
  />

  <div v-if="btItems">
    <div class="margin-top-xs medium-margin-bottom-m small-margin-bottom-s">
      <p>*PLEASE NOTE: Samples, prices and promotions may vary in stores.</p>
      <p class="legal-notice">
        Some samples listed as "available" may actually be represented in-store by another item from the same collection.
        Our associates will be happy to help you with additional color options and configurations. Because our inventory is always changing,
        stock status and sample availability cannot be guaranteed.
      </p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import { fireTag } from '@vue-util/tagManager';
import { FETCH_GEOCODE, RESET_STORE_ACTION, FETCH_STORES_BY_ZIPCODE } from '../types/actions';
import {
  SET_SEARCH_BY_ZIPCODE, SET_DIVISION_NUMBER, SET_RESULTS_DISPLAYED, SET_LOCATION_RADIUS, SET_PREFILLED_ZIPCODE,
  SET_IS_BRIDAL, SET_PAGE, SET_BAG_ITEMS, SET_UPC_NUMBER, SET_USE_FCC_API, SET_SHOW_ACCORDION,
  SET_CURBSIDE_HOURS, SET_AVAILABILITY, SET_FCC_MACYS_API_KEY, SET_SEARCH_LOCATION_BY_ADDRESS, SET_BT_ITEMS, SET_PRODUCT_ID, SET_AREA_RUG_ITEMS,
} from '../types/mutations';
import SearchBar from './SearchBar';
import Stores from './Stores';
import {
  UPS,
  LABELFREE,
  FEDEX,
  MSTORE,
} from '../types/constants';

export default {
  name: 'Locator',
  components: {
    SearchBar,
    Stores,
  },
  props: {
    typeOf: {
      type: String,
      default: 'UPS',
    },
    separateOutlets: {
      type: Boolean,
      default: false,
    },
    address: {
      type: String,
      default: null,
    },
    searchByZipCode: {
      type: Boolean,
      default: false,
    },
    divisionNumber: {
      type: Number,
      default: 12,
    },
    myStoreId: {
      type: Number,
      default: null,
    },
    resultsDisplayed: {
      type: Number,
      default: 100,
    },
    locationRadius: {
      type: Number,
      default: 100,
    },
    zipCode: {
      type: String,
      default: '',
    },
    isBridal: {
      type: Boolean,
      default: false,
    },
    useMspLocatorApi: {
      type: Boolean,
      default: false,
    },
    page: {
      type: String,
      default: '',
    },
    useFccApi: {
      type: Boolean,
      default: false,
    },
    bagItems: {
      type: Array,
      default: () => [],
    },
    fccMacysApiKey: {
      type: String,
      default: '',
    },
    upcNumber: {
      type: String,
      default: '',
    },
    showAccordion: {
      type: Boolean,
      default: false,
    },
    showCurbsideHours: {
      type: Boolean,
      default: false,
    },
    showAvailability: {
      type: Boolean,
      default: false,
    },
    searchLocationByAddress: {
      type: Boolean,
      default: false,
    },
    btItems: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: String,
      default: '',
    },
    areaRugItems: {
      type: Boolean,
      default: false,
    },
    showMyStoreCTA: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['storeSelected', 'storesFound', 'locationError', 'zipcodeSubmitted'],
  computed: {
    ...mapState('locations', ['isLoading', 'selectedStore', 'storesError', 'stores']),
    hasAreaRugItems() {
      return this.areaRugItems && this.typeOf === MSTORE;
    },
    myStoreButton() {
      return this.showMyStoreCTA && ![LABELFREE, UPS, FEDEX].includes(this.typeOf);
    },
  },
  watch: {
    selectedStore(val) {
      this.$emit('storeSelected', val);
    },
    stores(val) {
      this.$emit('storesFound', val.length);
    },
    storesError: {
      handler(val) {
        this.$emit('locationError', val);
      },
      deep: true,
    },
  },
  mounted() {
    this[SET_SEARCH_BY_ZIPCODE](this.searchByZipCode);
    this[SET_DIVISION_NUMBER](this.divisionNumber);
    this[SET_RESULTS_DISPLAYED](this.resultsDisplayed);
    this[SET_LOCATION_RADIUS](this.locationRadius);
    this[SET_PREFILLED_ZIPCODE](this.zipCode);
    this[SET_IS_BRIDAL](this.isBridal);
    this[SET_USE_FCC_API](this.useFccApi);
    this[SET_PAGE](this.page);
    this[SET_FCC_MACYS_API_KEY](this.fccMacysApiKey);
    this[SET_BAG_ITEMS](this.bagItems);
    this[SET_UPC_NUMBER](this.upcNumber);
    this[SET_SHOW_ACCORDION](this.showAccordion);
    this[SET_CURBSIDE_HOURS](this.showCurbsideHours);
    this[SET_AVAILABILITY](this.showAvailability);
    this[SET_SEARCH_LOCATION_BY_ADDRESS](this.searchLocationByAddress);
    this[SET_BT_ITEMS](this.btItems);
    this[SET_PRODUCT_ID](this.productId);
    this[SET_AREA_RUG_ITEMS](this.hasAreaRugItems);
    this.toggleHandler();
    this.fireLocatorTags();
  },
  methods: {
    ...mapActions('locations', [FETCH_GEOCODE, RESET_STORE_ACTION, FETCH_STORES_BY_ZIPCODE]),
    ...mapMutations('locations', [SET_SEARCH_BY_ZIPCODE, SET_DIVISION_NUMBER, SET_RESULTS_DISPLAYED, SET_LOCATION_RADIUS, SET_PREFILLED_ZIPCODE,
      SET_IS_BRIDAL, SET_PAGE, SET_BAG_ITEMS, SET_UPC_NUMBER, SET_USE_FCC_API, SET_PRODUCT_ID,
      SET_SHOW_ACCORDION, SET_CURBSIDE_HOURS, SET_AVAILABILITY, SET_FCC_MACYS_API_KEY, SET_SEARCH_LOCATION_BY_ADDRESS,
      SET_BT_ITEMS, SET_AREA_RUG_ITEMS]),
    toggleHandler() {
      this[RESET_STORE_ACTION]();
      if ((this.address || this.zipCode)) {
        this[FETCH_GEOCODE]({
          address: this.address || this.zipCode.split('-')[0],
          locationType: this.typeOf,
          useMspLocatorApi: this.useMspLocatorApi,
          separateOutlets: this.separateOutlets,
        });
      }
    },
    fireLocatorTags() {
      const tagObj = {
        action_location: this.page === 'bag' ? `${this.page}-body`.toUpperCase() : `${this.page}-body`,
        action_misc: this.myStoreId,
        site_action: 'store change start',
        postalCode: this.zipCode,
      };
      if (this.page === 'bag' || this.page === 'pdp') {
        tagObj.event_name = this.page === 'bag' ? 'bag search store' : 'pdp check store';
        tagObj.product_id = [this.productId];
      }
      if (this.page === 'plp' || this.page === 'browse') {
        tagObj.action_location = 'left nav';
      }
      fireTag('link', tagObj);
    },
  },
};
</script>

<style lang="scss" scoped>
.legal-notice{
  font-size: 12px;
  font-weight: 400;
  height: 72px;
}
</style>
